import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AddFavoritePoolResponse } from '@/services/poolService';
import { poolService } from '@/services/poolService';
import { DeleteOfferPayload, OfferPayload, RespondToOfferPayload } from '@/types/offer';
import { PoolFilters } from '@/types/pool';
import { CreatePoolPayload, UpdatePoolPayload, UpsertDraftPoolPayload } from '@/types/pool/create';
import { notificationKeys } from './useNotificationQueries';

// Query keys
export const poolKeys = {
  all: ['pools'] as const,
  lists: () => [...poolKeys.all, 'list'] as const,
  overview: () => [...poolKeys.all, 'overview'] as const,
  details: () => [...poolKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...poolKeys.details(), id] as const,
  reviewer: () => [...poolKeys.all, 'reviewer'] as const,
  draftLists: () => [...poolKeys.all, 'draft', 'list'] as const,
  favorites: () => [...poolKeys.all, 'favorites'] as const,
};

/**
 * Get pool overview stats for the authenticated user (borrower or investor).
 * Returns only the fields sent by the backend;
 */
export function usePoolOverview() {
  return useQuery({
    queryKey: poolKeys.overview(),
    queryFn: async () => {
      const result = await poolService.getOverview();
      if (result.error) throw new Error(result.error.message);
      return { overviewStats: result.data?.overview ?? null };
    },
  });
}

/**
 * Get pools (for both borrowers and investors - same endpoint)
 */
export function usePools(page = 1, filters: PoolFilters = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...poolKeys.lists(), page, filters],
    queryFn: async () => {
      const result = await poolService.getPools(page, filters);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    enabled: options?.enabled ?? true,
    gcTime: 0,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/**
 * Get pool by ID (borrower view)
 */
export function usePool(poolId: number | undefined) {
  return useQuery({
    queryKey: poolKeys.detail(poolId!),
    queryFn: async () => {
      if (!poolId) return null;
      const result = await poolService.getPoolById(poolId);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    enabled: !!poolId,
    gcTime: 0,
    staleTime: 0,
  });
}

/**
 * Get draft pools (borrower only)
 */
export function useDraftPools(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: poolKeys.draftLists(),
    queryFn: async () => {
      const result = await poolService.getDraftPools();
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    enabled: options?.enabled ?? true,
    gcTime: 0,
    staleTime: 0,
  });
}

/**
 * Create pool mutation
 */
export function useCreatePool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (poolData: CreatePoolPayload) => {
      const result = await poolService.createPool(poolData);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      queryClient.invalidateQueries({ queryKey: poolKeys.overview() });
      queryClient.invalidateQueries({ queryKey: poolKeys.draftLists() });
    },
  });
}

/**
 * Create draft pool mutation (borrower only)
 */
export function useCreateDraftPool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpsertDraftPoolPayload) => {
      const result = await poolService.createDraftPool(payload);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: poolKeys.draftLists() });
    },
  });
}

/**
 * Update draft pool mutation (borrower only)
 */
export function useUpdateDraftPool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ payload, id }: { payload: UpsertDraftPoolPayload; id: number }) => {
      const result = await poolService.updateDraftPool(payload, id);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: poolKeys.draftLists() });
    },
  });
}

/**
 * Update pool mutation
 */
export function useUpdatePool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ poolId, updateData }: { poolId: number; updateData: Omit<UpdatePoolPayload, 'pool_id'> }) => {
      const result = await poolService.updatePool(poolId, updateData);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: poolKeys.detail(variables.poolId) });
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.byPoolId(variables.poolId) });
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({ queryKey: poolKeys.overview() });
    },
  });
}

/**
 * Delete pool mutation
 */
export function useDeletePool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (poolId: number) => {
      const result = await poolService.deletePool(poolId);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      queryClient.invalidateQueries({ queryKey: poolKeys.detail(variables) });
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      queryClient.invalidateQueries({ queryKey: poolKeys.overview() });
    },
  });
}

/**
 * Send offer mutation
 */
export function useSendOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (offerData: OfferPayload) => {
      const result = await poolService.sendOffer(offerData);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      queryClient.invalidateQueries({ queryKey: poolKeys.detail(variables.pool_id) });
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Respond to offer mutation
 */
export function useRespondToOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (offerData: RespondToOfferPayload) => {
      const result = await poolService.respondToOffer(offerData);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      queryClient.invalidateQueries({ queryKey: poolKeys.detail(variables.pool_id) });
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Delete offer mutation
 */
export function useDeleteOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (offerData: DeleteOfferPayload) => {
      const result = await poolService.deleteOffer(offerData);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      queryClient.invalidateQueries({ queryKey: poolKeys.detail(variables.pool_id) });
      queryClient.invalidateQueries({ queryKey: notificationKeys.byPoolId(variables.pool_id) });
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
    },
  });
}

/**
 * Add pool to favorites mutation (investor only). POST /api/pools/favorites/<pool_id>
 */
export function useAddFavoritePool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      poolId,
      isFavorite,
    }: {
      poolId: number;
      isFavorite: boolean;
    }): Promise<AddFavoritePoolResponse> => {
      const result = await poolService.addFavoritePool(poolId, isFavorite);
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: (_, { poolId }) => {
      queryClient.invalidateQueries({ queryKey: poolKeys.lists() });
      queryClient.invalidateQueries({ queryKey: poolKeys.detail(poolId) });
      queryClient.invalidateQueries({ queryKey: poolKeys.favorites() });
    },
  });
}
